'use client';

import { useState } from 'react';

import { PublicLink } from '@/components/PublicLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatedContainer } from '@/components/ui/animated-container';
import {
  PageHero,
  PageIntro,
  PageSection,
  SurfacePanel,
  UtilityPageShell,
} from '@/components/site/page-shell';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Введите корректный адрес электронной почты');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/user/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error || 'Не удалось отправить письмо');
      }

      setIsSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UtilityPageShell
      hero={
        <PageHero>
          <PageIntro
            eyebrow="Аккаунт"
            title="Сброс пароля"
            description="Введите адрес электронной почты, и мы отправим ссылку для восстановления доступа."
            align="center"
            compact
          />
        </PageHero>
      }
    >
      <PageSection>
        <AnimatedContainer animation="none">
          <SurfacePanel className="mx-auto max-w-md">
            {isSent ? (
              <div className="space-y-4 text-center">
                <div className="text-emerald-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-[var(--site-ink-strong)]">Проверьте почту</h2>
                <p className="text-sm leading-6 text-[var(--site-ink-muted)]">
                  Если аккаунт с таким адресом существует, мы отправили письмо со ссылкой для сброса пароля. Ссылка действует 1 час.
                </p>
                <div className="pt-2">
                  <PublicLink href="/login">
                    <Button variant="outline" className="w-full">Вернуться ко входу</Button>
                  </PublicLink>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" noValidate>
                <div className="text-center">
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
                    Забыли пароль?
                  </h2>
                  <p className="mt-2 text-sm text-[var(--site-ink-muted)]">
                    Укажите вашу электронную почту, и мы отправим инструкции по сбросу.
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="forgot-email" className="text-sm font-medium leading-none">
                    Электронная почта
                  </label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="ivan@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    aria-invalid={Boolean(error)}
                  />
                  {error ? <p className="text-sm text-destructive">{error}</p> : null}
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Отправляем...' : 'Отправить ссылку для сброса'}
                </Button>

                <div className="text-center text-sm">
                  <p className="text-[var(--site-ink-muted)]">
                    Вспомнили пароль?{' '}
                    <PublicLink href="/login" className="font-medium text-[var(--site-accent-text)] hover:underline">
                      Войти
                    </PublicLink>
                  </p>
                </div>
              </form>
            )}
          </SurfacePanel>
        </AnimatedContainer>
      </PageSection>
    </UtilityPageShell>
  );
}
